import React, { useState, useEffect } from "react";
import { CenterWrapper, Content, ContentHeader, LabelContainer, StyledHeading, Section } from "./styles";
import TextView from "../../TextView";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { Tooltip, IconButton, tooltipClasses, styled } from "@mui/material";
import { useSelector } from "react-redux";
import { radioFields, followersVisibilityFields, getFollowPrivacyDisplay } from "./fields";
import { useUserUpdateProfile } from "../../../services/mutations";
import Button from "../../Button";
import { toast } from 'react-toastify';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { editUserProfileAction } from "../../../redux/actions";

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#D4E2F2E5",
        color: "#123051",
        fontSize: "14px",
        padding: "4px 8px",
        borderRadius: "4px",
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: "#D4E2F2E5",
        marginTop: "8px",
    }
}));

const CustomRadio = styled(Radio)({
    color: "#9B5AF8",
    "&.Mui-checked": {
        color: "#9B5AF8",
    },
    "&:hover": {
        backgroundColor: "rgba(155, 90, 248, 0.1)",
        borderRadius: "50%",
    },
});

export const Visibility = () => {
    const userState = useSelector((state) => state.userProfile);
    const { userProfile } = userState;

    const dispatch = useDispatch();

    const editProfile = useUserUpdateProfile();

    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState(userProfile?.visibility || "PUBLIC");
    const [followersVisibility, setFollowersVisibility] = useState(userProfile?.follow_privacy ?? "EVERYONE");

    useEffect(() => {
        if (userProfile?.visibility) setValue(userProfile.visibility);
        if (userProfile?.follow_privacy != null) setFollowersVisibility(userProfile.follow_privacy);
    }, [userProfile?.visibility, userProfile?.follow_privacy]);

    const handleChange = (event) => {
        setValue(event.target.value);
    };

    const handleFollowersVisibilityChange = (event) => {
        setFollowersVisibility(event.target.value);
    };

    const hasVisibilityChanged = value !== userProfile?.visibility;
    const hasFollowPrivacyChanged = followersVisibility !== (userProfile?.follow_privacy ?? "EVERYONE");
    const hasUnsavedChanges = hasVisibilityChanged || hasFollowPrivacyChanged;

    const saveVisibility = () => {
        const followPrivacyDisplay = getFollowPrivacyDisplay(followersVisibility);
        setLoading(true);
        editProfile.mutateAsync(
            {
                user_profile: {
                    visibility: value,
                    follow_privacy: followersVisibility,
                    follow_privacy_display: followPrivacyDisplay,
                }
            },
            {
                onSuccess: () => {
                    dispatch(
                        editUserProfileAction({
                            visibility: value,
                            follow_privacy: followersVisibility,
                            follow_privacy_display: followPrivacyDisplay,
                        })
                    );
                    toast.success("Visibility updated successfully");
                    setLoading(false);
                },
                onError: (error) => {
                    setLoading(false);
                    toast.error("Visibility update failed");
                }
            }
        );
    }

    return (
        <CenterWrapper>
            <ContentHeader>
                <TextView variant="title">Visibility</TextView>
            </ContentHeader>

            <Content>
                <StyledHeading>Set your account’s visibility here: </StyledHeading>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="demo-controlled-radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={value}
                        onChange={handleChange}
                    >
                        {radioFields.map((field) => (
                            <FormControlLabel
                                key={field.id}
                                value={field.value}
                                control={<CustomRadio />}
                                label={
                                    <LabelContainer>
                                        {field.label}
                                        {field.showInfo && (
                                            <CustomTooltip title={field.infoMessage} arrow placement="right-start">
                                                <IconButton size="small">
                                                    <InfoOutlinedIcon fontSize="small" color="#BBBBBB" />
                                                </IconButton>
                                            </CustomTooltip>
                                        )}
                                    </LabelContainer>
                                }
                            />
                        ))}
                    </RadioGroup>
                </FormControl>

                <Section>
                    <StyledHeading>Control who can see your followers/followings on your gallery.</StyledHeading>
                    <FormControl>
                        <RadioGroup
                            aria-labelledby="followers-visibility-radio"
                            name="followers-visibility"
                            value={followersVisibility}
                            onChange={handleFollowersVisibilityChange}
                        >
                            {followersVisibilityFields.map((field) => (
                                <FormControlLabel
                                    key={field.id}
                                    value={field.value}
                                    control={<CustomRadio />}
                                    label={field.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Section>

                <Button
                    style={{ width: "150px", marginTop: "10px" }}
                    onClick={saveVisibility}
                    isLoading={loading}
                    disabled={!hasUnsavedChanges}
                >
                    Save
                </Button>
            </Content>
        </CenterWrapper>
    )
}